import {
  NOTES_LIST,
  NOTES_LIST_SUCCESS,
  NOTES_LIST_FAILURE,
  NOTE_GET,
  NOTE_GET_SUCCESS,
  NOTE_GET_FAILURE,
  NOTE_CREATE,
  NOTE_CREATE_SUCCESS,
  NOTE_CREATE_FAILURE,
  NOTE_UPDATE,
  NOTE_UPDATE_SUCCESS,
  NOTE_UPDATE_FAILURE,
  NOTE_UPDATE_CONFLICT,
  NOTE_DELETE,
  NOTE_DELETE_SUCCESS,
  NOTE_DELETE_FAILURE,
  NOTE_RESTORE,
  NOTE_RESTORE_SUCCESS,
  NOTE_RESTORE_FAILURE,
  NOTE_TAGS_LIST,
  NOTE_TAGS_LIST_SUCCESS,
  NOTE_TAGS_LIST_FAILURE,
  NOTE_TAG_CREATE,
  NOTE_TAG_CREATE_SUCCESS,
  NOTE_TAG_CREATE_FAILURE,
  NOTE_TAG_DELETE,
  NOTE_TAG_DELETE_SUCCESS,
  NOTE_TAG_DELETE_FAILURE,
  NOTE_SET_TAGS,
  NOTE_SET_TAGS_SUCCESS,
  NOTE_SET_TAGS_FAILURE,
  NOTE_SHARES_LIST,
  NOTE_SHARES_LIST_SUCCESS,
  NOTE_SHARES_LIST_FAILURE,
  NOTE_SHARE_SET,
  NOTE_SHARE_SET_SUCCESS,
  NOTE_SHARE_SET_FAILURE,
  NOTE_SHARE_REVOKE,
  NOTE_SHARE_REVOKE_SUCCESS,
  NOTE_SHARE_REVOKE_FAILURE,
  NOTE_MEDIA_UPLOAD,
  NOTE_LEAVE,
  NOTE_LEAVE_SUCCESS,
  NOTE_LEAVE_FAILURE,
  NOTE_SHARE_CANDIDATES,
  NOTE_SHARE_CANDIDATES_SUCCESS,
  NOTE_SHARE_CANDIDATES_FAILURE,
  NOTES_FILTER_SET,
  NOTE_CLEAR,
  NOTE_SAVE_STATE,
} from '../constants';

/**
 * Notes actions.
 *
 * Callbacks (`cb`) are used where a component needs the response inline — the editor has
 * to know a media upload's id before it can insert a node, and the sidebar has to know a
 * create succeeded before it navigates. Everything else flows through the reducer.
 */
export default class NotesAction {
  // ─── list ─────────────────────────────────────────────────────────────────
  /** payload: { tab?, search?, tags?, limit?, offset? } — offset 0 replaces, higher appends. */
  static NotesList(payload, cb) {
    return { type: NOTES_LIST, payload, cb };
  }
  static NotesListSuccess(payload) {
    return { type: NOTES_LIST_SUCCESS, payload };
  }
  static NotesListFailure() {
    return { type: NOTES_LIST_FAILURE };
  }

  // ─── one note ─────────────────────────────────────────────────────────────
  /** payload: { id } */
  static NoteGet(payload, cb) {
    return { type: NOTE_GET, payload, cb };
  }
  static NoteGetSuccess(payload) {
    return { type: NOTE_GET_SUCCESS, payload };
  }
  static NoteGetFailure(payload) {
    return { type: NOTE_GET_FAILURE, payload };
  }

  /** payload: { title?, content?, color?, icon? } */
  static NoteCreate(payload, cb) {
    return { type: NOTE_CREATE, payload, cb };
  }
  static NoteCreateSuccess(payload) {
    return { type: NOTE_CREATE_SUCCESS, payload };
  }
  static NoteCreateFailure() {
    return { type: NOTE_CREATE_FAILURE };
  }

  /**
   * payload: { id, version?, content?, title?, color?, icon?, is_pinned?, is_archived? }
   *
   * `version` is REQUIRED when `content` is present — that is the optimistic-concurrency
   * guard, and the server refuses a content write without it.
   */
  static NoteUpdate(payload, cb) {
    return { type: NOTE_UPDATE, payload, cb };
  }
  static NoteUpdateSuccess(payload) {
    return { type: NOTE_UPDATE_SUCCESS, payload };
  }
  static NoteUpdateFailure(payload) {
    return { type: NOTE_UPDATE_FAILURE, payload };
  }
  /** payload: the note as it currently stands on the server, for "keep mine / take theirs". */
  static NoteUpdateConflict(payload) {
    return { type: NOTE_UPDATE_CONFLICT, payload };
  }

  /** payload: { id } */
  static NoteDelete(payload, cb) {
    return { type: NOTE_DELETE, payload, cb };
  }
  static NoteDeleteSuccess(payload) {
    return { type: NOTE_DELETE_SUCCESS, payload };
  }
  static NoteDeleteFailure() {
    return { type: NOTE_DELETE_FAILURE };
  }

  /** payload: { id } */
  static NoteRestore(payload, cb) {
    return { type: NOTE_RESTORE, payload, cb };
  }
  static NoteRestoreSuccess(payload) {
    return { type: NOTE_RESTORE_SUCCESS, payload };
  }
  static NoteRestoreFailure() {
    return { type: NOTE_RESTORE_FAILURE };
  }

  // ─── tags ─────────────────────────────────────────────────────────────────
  static NoteTagsList(payload, cb) {
    return { type: NOTE_TAGS_LIST, payload, cb };
  }
  static NoteTagsListSuccess(payload) {
    return { type: NOTE_TAGS_LIST_SUCCESS, payload };
  }
  static NoteTagsListFailure() {
    return { type: NOTE_TAGS_LIST_FAILURE };
  }

  /** payload: { name, color? } — idempotent; an existing name returns that tag. */
  static NoteTagCreate(payload, cb) {
    return { type: NOTE_TAG_CREATE, payload, cb };
  }
  static NoteTagCreateSuccess(payload) {
    return { type: NOTE_TAG_CREATE_SUCCESS, payload };
  }
  static NoteTagCreateFailure() {
    return { type: NOTE_TAG_CREATE_FAILURE };
  }

  /** payload: { tag_id } */
  static NoteTagDelete(payload, cb) {
    return { type: NOTE_TAG_DELETE, payload, cb };
  }
  static NoteTagDeleteSuccess(payload) {
    return { type: NOTE_TAG_DELETE_SUCCESS, payload };
  }
  static NoteTagDeleteFailure() {
    return { type: NOTE_TAG_DELETE_FAILURE };
  }

  /** payload: { id, tag_ids } — a whole-set replace of MY tags on that note. */
  static NoteSetTags(payload, cb) {
    return { type: NOTE_SET_TAGS, payload, cb };
  }
  static NoteSetTagsSuccess(payload) {
    return { type: NOTE_SET_TAGS_SUCCESS, payload };
  }
  static NoteSetTagsFailure() {
    return { type: NOTE_SET_TAGS_FAILURE };
  }

  // ─── sharing ──────────────────────────────────────────────────────────────
  /** payload: { id } */
  static NoteSharesList(payload, cb) {
    return { type: NOTE_SHARES_LIST, payload, cb };
  }
  static NoteSharesListSuccess(payload) {
    return { type: NOTE_SHARES_LIST_SUCCESS, payload };
  }
  static NoteSharesListFailure() {
    return { type: NOTE_SHARES_LIST_FAILURE };
  }

  /** payload: { id, user_id, permission } */
  static NoteShareSet(payload, cb) {
    return { type: NOTE_SHARE_SET, payload, cb };
  }
  static NoteShareSetSuccess(payload) {
    return { type: NOTE_SHARE_SET_SUCCESS, payload };
  }
  static NoteShareSetFailure(payload) {
    return { type: NOTE_SHARE_SET_FAILURE, payload };
  }

  /** payload: { id, user_id } */
  static NoteShareRevoke(payload, cb) {
    return { type: NOTE_SHARE_REVOKE, payload, cb };
  }
  static NoteShareRevokeSuccess(payload) {
    return { type: NOTE_SHARE_REVOKE_SUCCESS, payload };
  }
  static NoteShareRevokeFailure() {
    return { type: NOTE_SHARE_REVOKE_FAILURE };
  }

  /**
   * Give up my own access to a note shared with me. payload: { id }
   *
   * Not a revoke aimed at myself — revoke is owner-only, and this is the recipient's exit.
   * The note stays where it is for everyone else.
   */
  static NoteLeave(payload, cb) {
    return { type: NOTE_LEAVE, payload, cb };
  }
  static NoteLeaveSuccess(payload) {
    return { type: NOTE_LEAVE_SUCCESS, payload };
  }
  static NoteLeaveFailure() {
    return { type: NOTE_LEAVE_FAILURE };
  }

  // ─── media ────────────────────────────────────────────────────────────────
  /**
   * payload: { id, file } — the editor needs the media id back before it can insert a
   * node, so this one is callback-only and writes nothing to the store.
   */
  static NoteMediaUpload(payload, cb) {
    return { type: NOTE_MEDIA_UPLOAD, payload, cb };
  }

  /**
   * Colleagues this user could share with. payload: { limit?, offset? }
   *
   * Deliberately the company-users endpoint and not a global user search: a grant is
   * refused unless both users share a company, so offering anyone else would be offering
   * an action that cannot succeed.
   */
  static NoteShareCandidates(payload, cb) {
    return { type: NOTE_SHARE_CANDIDATES, payload, cb };
  }
  static NoteShareCandidatesSuccess(payload) {
    return { type: NOTE_SHARE_CANDIDATES_SUCCESS, payload };
  }
  static NoteShareCandidatesFailure() {
    return { type: NOTE_SHARE_CANDIDATES_FAILURE };
  }

  // ─── local ────────────────────────────────────────────────────────────────
  /** payload: { tab?, search?, tags? } — merged into the current filter. */
  static NotesFilterSet(payload) {
    return { type: NOTES_FILTER_SET, payload };
  }
  static NoteClear() {
    return { type: NOTE_CLEAR };
  }
  /** payload: 'idle' | 'saving' | 'saved' | 'error' */
  static NoteSaveState(payload) {
    return { type: NOTE_SAVE_STATE, payload };
  }
}
